import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Pagination,
  Tab,
  Tabs,
  Spinner,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FaFileExcel, FaSearch, FaEraser } from 'react-icons/fa';
import * as api from '~/Services/Service';
import { listarGruposEmpresaAdmin } from '~/Services/Service';
import { salvarEmExcel } from '~/Utils/Helpers';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import ModalLiberacao from './ModalLiberacao';

const ITEMS_POR_PAGINA = 15;

const ComissaoRetida = () => {
  const [ges, setGes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);

  const [geSelecionada, setGeSelecionada] = useState(null);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [empreendimentosSelecionados, setEmpreendimentosSelecionados] = useState([]);
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [status, setStatus] = useState('N');

  const [dados, setDados] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);

  const [vendasPendentes, setVendasPendentes] = useState([]);
  const [loadingVendas, setLoadingVendas] = useState(false);

  const [modalLiberacao, setModalLiberacao] = useState({ visivel: false, prevenda: null });
  const [tabAtiva, setTabAtiva] = useState('relatorio');

  useEffect(() => {
    let ativo = true;
    const carregarGes = async () => {
      try {
        const res = await listarGruposEmpresaAdmin({});
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : (res && res.itens ? res.itens : []);
        setGes(lista.map((g) => ({
          value: g.codigo_grupo_empresa,
          label: g.apelido || g.descricao || `GE ${g.codigo_grupo_empresa}`,
        })));
      } catch (err) {
        if (ativo) {
          toast.error('Erro ao carregar GEs.');
        }
      }
    };
    carregarGes();
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    if (!geSelecionada) {
      setClientes([]);
      setEmpreendimentos([]);
      setClienteSelecionado(null);
      setEmpreendimentosSelecionados([]);
      return;
    }
    let ativo = true;
    const carregarFiltros = async () => {
      try {
        const [respClientes, respEmps] = await Promise.all([
          api.consultarClientesIncorporadores(geSelecionada.value),
          api.consultarEmpreendimentosComissao(geSelecionada.value, ''),
        ]);
        if (!ativo) return;
        const listaClientes = Array.isArray(respClientes) ? respClientes : (respClientes && respClientes.itens ? respClientes.itens : []);
        setClientes(listaClientes.map((c) => ({ value: c.codigo_conta, label: c.apelido_conta })));
        const listaEmps = Array.isArray(respEmps) ? respEmps : (respEmps && respEmps.itens ? respEmps.itens : []);
        setEmpreendimentos(listaEmps.map((e) => ({ value: e.codigo_familia, label: e.nome_familia })));
      } catch (err) {
        if (ativo) {
          toast.error('Erro ao carregar filtros.');
        }
      }
    };
    carregarFiltros();
    return () => { ativo = false; };
  }, [geSelecionada]);

  const filtrosValidos = () => {
    if (!geSelecionada) {
      toast.warn('Selecione uma G.E.');
      return false;
    }
    if (!empreendimentosSelecionados || empreendimentosSelecionados.length === 0) {
      toast.warn('Selecione ao menos um empreendimento.');
      return false;
    }
    if (dataInicial && dataFinal && dataInicial > dataFinal) {
      toast.warn('A data inicial deve ser menor ou igual à data final.');
      return false;
    }

    return true;
  };

  const montarParamsConsulta = (paginaAtual, tamanhoPagina) => ({
    ge: geSelecionada.value,
    conta: clienteSelecionado ? clienteSelecionado.value : '',
    familias: empreendimentosSelecionados.map((e) => e.value).join(','),
    data_ini: dataInicial,
    data_fim: dataFinal,
    status,
    pagina: paginaAtual,
    tamanho_pagina: tamanhoPagina,
  });

  const consultar = async (paginaAtual) => {
    if (!filtrosValidos()) return;

    setLoading(true);
    try {
      const params = montarParamsConsulta(paginaAtual, ITEMS_POR_PAGINA);
      const resp = await api.consultarComissaoRetida(params);
      setDados(resp);
      setTotalRegistros(resp && resp.total ? resp.total : 0);
      setPagina(paginaAtual);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao consultar relatório.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const carregarVendasPendentes = async () => {
    if (!geSelecionada) return;
    setLoadingVendas(true);
    try {
      const resp = await api.consultarVendasPendentesComissao(geSelecionada.value);
      setVendasPendentes(resp && resp.itens ? resp.itens : []);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao carregar vendas pendentes.';
      toast.error(msg);
    } finally {
      setLoadingVendas(false);
    }
  };

  const handleTabSelect = (key) => {
    setTabAtiva(key);
    if (key === 'vendas-pendentes' && geSelecionada) {
      carregarVendasPendentes();
    }
  };

  const handleConsultar = () => {
    consultar(1);
  };

  const handleLimpar = () => {
    setGeSelecionada(null);
    setClienteSelecionado(null);
    setEmpreendimentosSelecionados([]);
    setDataInicial('');
    setDataFinal('');
    setStatus('N');
    setDados(null);
    setPagina(1);
    setTotalRegistros(0);
    setVendasPendentes([]);
  };

  const handleExportarExcel = async () => {
    if (!filtrosValidos()) return;
    if (!dados || !dados.itens || dados.itens.length === 0) {
      toast.warn('Nenhum dado para exportar.');
      return;
    }
    setExportando(true);
    try {
      const totalParaExportar = totalRegistros || dados.itens.length;
      const resp = await api.consultarComissaoRetida(montarParamsConsulta(1, totalParaExportar));
      const itens = resp && resp.itens && resp.itens.length > 0 ? resp.itens : dados.itens;
      await salvarEmExcel(itens, '/exportador/excel/comissao-retida');
      toast.success('Relatório exportado com sucesso!');
    } catch (err) {
      toast.error('Erro ao exportar para Excel.');
    } finally {
      setExportando(false);
    }
  };

  const handleSelecionarTodos = () => {
    setEmpreendimentosSelecionados(empreendimentos);
  };

  const handlePaginaChange = (novaPagina) => {
    consultar(novaPagina);
  };

  const handleLiberado = () => {
    setModalLiberacao({ visivel: false, prevenda: null });
    if (geSelecionada) {
      carregarVendasPendentes();
    }
  };

  const formatarValor = (valor) => {
    if (valor == null) return '0,00';
    return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatarData = (data) => {
    if (!data) return '';
    return moment(data).format('DD/MM/YYYY');
  };

  const renderPaginacao = () => {
    const totalPaginas = Math.ceil(totalRegistros / ITEMS_POR_PAGINA);
    if (totalPaginas <= 1) return null;

    const maxVisivel = 5;
    let inicio = Math.max(1, pagina - Math.floor(maxVisivel / 2));
    let fim = Math.min(totalPaginas, inicio + maxVisivel - 1);
    if (fim - inicio < maxVisivel - 1) {
      inicio = Math.max(1, fim - maxVisivel + 1);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <span>
          Mostrando {Math.min((pagina - 1) * ITEMS_POR_PAGINA + 1, totalRegistros)} até{' '}
          {Math.min(pagina * ITEMS_POR_PAGINA, totalRegistros)} de {totalRegistros} registros
        </span>
        <Pagination className="mb-0">
          <Pagination.First onClick={() => handlePaginaChange(1)} disabled={pagina === 1} />
          <Pagination.Prev onClick={() => handlePaginaChange(pagina - 1)} disabled={pagina === 1} />
          {inicio > 1 && <Pagination.Ellipsis disabled />}
          {Array.from({ length: fim - inicio + 1 }, (_, i) => inicio + i).map((p) => (
            <Pagination.Item key={p} active={p === pagina} onClick={() => handlePaginaChange(p)}>
              {p}
            </Pagination.Item>
          ))}
          {fim < totalPaginas && <Pagination.Ellipsis disabled />}
          <Pagination.Next onClick={() => handlePaginaChange(pagina + 1)} disabled={pagina === totalPaginas} />
          <Pagination.Last onClick={() => handlePaginaChange(totalPaginas)} disabled={pagina === totalPaginas} />
        </Pagination>
      </div>
    );
  };

  const renderTabelaRelatorio = () => {
    if (!dados || !dados.itens || dados.itens.length === 0) {
      return <p className="text-muted mt-3">Nenhum resultado encontrado.</p>;
    }

    let totalValor = 0;
    let totalLiquido = 0;
    const linhas = [];
    let incorporadorAtual = '';
    let empreendimentoAtual = '';
    let subEmpValor = 0;
    let subEmpLiquido = 0;
    let subEmpQtd = 0;
    let subIncValor = 0;
    let subIncLiquido = 0;
    let subIncQtd = 0;

    const inserirSubtotalEmpreendimento = () => {
      if (!empreendimentoAtual) return;
      linhas.push({
        tipo: 'subtotal-empreendimento',
        dados: { quantidade: subEmpQtd, total_valor: subEmpValor, total_liquido: subEmpLiquido },
        key: `sub-emp-${incorporadorAtual}-${empreendimentoAtual}`,
      });
    };

    const inserirSubtotalIncorporador = () => {
      if (!incorporadorAtual) return;
      linhas.push({
        tipo: 'subtotal-incorporador',
        dados: {
          incorporador: incorporadorAtual,
          quantidade: subIncQtd,
          total_valor: subIncValor,
          total_liquido: subIncLiquido,
        },
        key: `sub-inc-${incorporadorAtual}`,
      });
    };

    dados.itens.forEach((item) => {
      const incorporador = item.nome_incorporador || '';
      const empreendimento = item.empreendimento || '';

      if (incorporadorAtual && (incorporador !== incorporadorAtual || empreendimento !== empreendimentoAtual)) {
        inserirSubtotalEmpreendimento();

        if (incorporador !== incorporadorAtual) {
          inserirSubtotalIncorporador();
          subIncValor = 0;
          subIncLiquido = 0;
          subIncQtd = 0;
        }

        subEmpValor = 0;
        subEmpLiquido = 0;
        subEmpQtd = 0;
      }

      incorporadorAtual = incorporador;
      empreendimentoAtual = empreendimento;

      const val = Number(item.valor) || 0;
      const liq = Number(item.liquido) || 0;
      totalValor += val;
      totalLiquido += liq;
      subEmpValor += val;
      subEmpLiquido += liq;
      subEmpQtd += 1;
      subIncValor += val;
      subIncLiquido += liq;
      subIncQtd += 1;
      linhas.push({
        tipo: 'item',
        dados: item,
        key: `item-${item.codigo_titulo}-${item.proposta}-${item.data_vencimento}`,
      });
    });

    if (incorporadorAtual) {
      inserirSubtotalEmpreendimento();
      inserirSubtotalIncorporador();
    }

    return (
      <>
        <Table striped bordered hover size="sm" responsive className="mt-3">
          <thead>
            <tr className="text-center">
              <th>Incorporador</th>
              <th>Empreendimento</th>
              <th>Proposta</th>
              <th>Título</th>
              <th>Vencimento</th>
              <th>Valor</th>
              <th>Líquido</th>
              <th>Baixado</th>
              <th>Quadra</th>
              <th>Lote</th>
              <th>Cliente</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) => {
              if (linha.tipo === 'subtotal-empreendimento') {
                return (
                  <tr key={linha.key} style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                    <td colSpan={5} className="text-right">
                      Subtotal Empreendimento ({linha.dados.quantidade} títulos)
                    </td>
                    <td className="text-right">{formatarValor(linha.dados.total_valor)}</td>
                    <td className="text-right">{formatarValor(linha.dados.total_liquido)}</td>
                    <td colSpan={4} />
                  </tr>
                );
              }
              if (linha.tipo === 'subtotal-incorporador') {
                return (
                  <tr key={linha.key} style={{ backgroundColor: '#d9edf7', fontWeight: 'bold' }}>
                    <td colSpan={5} className="text-right">
                      Subtotal Incorporador - {linha.dados.incorporador} ({linha.dados.quantidade} títulos)
                    </td>
                    <td className="text-right">{formatarValor(linha.dados.total_valor)}</td>
                    <td className="text-right">{formatarValor(linha.dados.total_liquido)}</td>
                    <td colSpan={4} />
                  </tr>
                );
              }
              const item = linha.dados;
              return (
                <tr key={linha.key}>
                  <td>{item.nome_incorporador}</td>
                  <td>{item.empreendimento}</td>
                  <td className="text-center">{item.proposta}</td>
                  <td className="text-center">{item.codigo_titulo}</td>
                  <td className="text-center">{formatarData(item.data_vencimento)}</td>
                  <td className="text-right">{formatarValor(item.valor)}</td>
                  <td className="text-right">{formatarValor(item.liquido)}</td>
                  <td className="text-center">{item.baixado === 'S' ? 'Sim' : 'Não'}</td>
                  <td className="text-center">{item.quadra}</td>
                  <td className="text-center">{item.lote}</td>
                  <td>{item.nome_cliente}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 'bold', backgroundColor: '#e9ecef' }}>
              <td colSpan={5} className="text-right">Total Geral</td>
              <td className="text-right">{formatarValor(totalValor)}</td>
              <td className="text-right">{formatarValor(totalLiquido)}</td>
              <td colSpan={4} />
            </tr>
          </tfoot>
        </Table>
        {renderPaginacao()}
      </>
    );
  };

  const renderTabelaVendasPendentes = () => {
    if (loadingVendas) {
      return (
        <div className="text-center mt-3">
          <Spinner animation="border" size="sm" /> Carregando...
        </div>
      );
    }
    if (!vendasPendentes || vendasPendentes.length === 0) {
      return <p className="text-muted mt-3">Nenhuma venda com pendência.</p>;
    }

    return (
      <Table striped bordered hover size="sm" responsive className="mt-3">
        <thead>
          <tr className="text-center">
            <th>Pré-Venda</th>
            <th>Empreendimento</th>
            <th>Quadra</th>
            <th>Lote</th>
            <th>Cliente</th>
            <th>Data</th>
            <th>Ação</th>
          </tr>
        </thead>
        <tbody>
          {vendasPendentes.map((vp) => (
            <tr key={`vp-${vp.prevenda}`}>
              <td className="text-center">{vp.prevenda}</td>
              <td>{vp.empreendimento}</td>
              <td className="text-center">{vp.quadra}</td>
              <td className="text-center">{vp.lote}</td>
              <td>{vp.nome_cliente}</td>
              <td className="text-center">{formatarData(vp.data_venda)}</td>
              <td className="text-center">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setModalLiberacao({ visivel: true, prevenda: vp.prevenda })}
                >
                  Liberar
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <Container fluid className="p-3">
      <h4 className="mb-3">Comissão Retida</h4>

      <Card className="mb-3">
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          Filtros
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4} className="mb-2">
              <Form.Label>G.E.</Form.Label>
              <Select
                options={ges}
                value={geSelecionada}
                onChange={(opt) => setGeSelecionada(opt)}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Selecione..."
                isClearable
              />
            </Col>
            <Col md={4} className="mb-2">
              <Form.Label>Cliente Incorporador</Form.Label>
              <Select
                options={clientes}
                value={clienteSelecionado}
                onChange={(opt) => setClienteSelecionado(opt)}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Todos..."
                isClearable
                isDisabled={!geSelecionada}
              />
            </Col>
            <Col md={4} className="mb-2">
              <Form.Label>
                Empreendimentos{' '}
                <Button variant="link" size="sm" className="p-0" onClick={handleSelecionarTodos} disabled={empreendimentos.length === 0}>
                  (Selecionar Todos)
                </Button>
              </Form.Label>
              <Select
                isMulti
                options={empreendimentos}
                value={empreendimentosSelecionados}
                onChange={(opts) => setEmpreendimentosSelecionados(opts || [])}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Selecione..."
                isDisabled={!geSelecionada}
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={3} className="mb-2">
              <Form.Label>Data Vencimento Inicial</Form.Label>
              <Form.Control
                type="date"
                value={dataInicial}
                onChange={(e) => setDataInicial(e.target.value)}
              />
            </Col>
            <Col md={3} className="mb-2">
              <Form.Label>Data Vencimento Final</Form.Label>
              <Form.Control
                type="date"
                value={dataFinal}
                onChange={(e) => setDataFinal(e.target.value)}
              />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Status</Form.Label>
              <div className="d-flex align-items-center" style={{ height: '38px' }}>
                <Form.Check
                  inline
                  type="radio"
                  label="Em Aberto"
                  name="status"
                  value="N"
                  checked={status === 'N'}
                  onChange={(e) => setStatus(e.target.value)}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Baixados"
                  name="status"
                  value="S"
                  checked={status === 'S'}
                  onChange={(e) => setStatus(e.target.value)}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Ambos"
                  name="status"
                  value="A"
                  checked={status === 'A'}
                  onChange={(e) => setStatus(e.target.value)}
                />
              </div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col className="d-flex">
              <Button variant="primary" onClick={handleConsultar} disabled={loading} className="mr-2">
                <FaSearch className="mr-1" />
                {loading ? 'Consultando...' : 'Consultar'}
              </Button>
              <Button variant="secondary" onClick={handleLimpar} className="mr-2">
                <FaEraser className="mr-1" />
                Limpar
              </Button>
              <Button variant="success" onClick={handleExportarExcel} disabled={exportando || !dados}>
                <FaFileExcel className="mr-1" />
                {exportando ? 'Exportando...' : 'Exportar Excel'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Tabs activeKey={tabAtiva} onSelect={handleTabSelect}>
            <Tab eventKey="relatorio" title="Relatório">
              {loading ? (
                <div className="text-center mt-3">
                  <Spinner animation="border" size="sm" /> Carregando...
                </div>
              ) : (
                renderTabelaRelatorio()
              )}
            </Tab>
            <Tab eventKey="vendas-pendentes" title="Vendas com Pendência">
              {renderTabelaVendasPendentes()}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <ModalLiberacao
        visivel={modalLiberacao.visivel}
        prevenda={modalLiberacao.prevenda}
        onFechar={() => setModalLiberacao({ visivel: false, prevenda: null })}
        onLiberado={handleLiberado}
      />
    </Container>
  );
};

export default ComissaoRetida;
